// Screen: Room — chiamata attiva.
// Fedele al mockup 02 ratificato: topbar minimale, griglia, drawer (chat + persone),
// controlli icona flottanti, toast notifiche, active speaker oro, REC indicator.

import { Device } from 'mediasoup-client';
import type { types as MC } from 'mediasoup-client';
import { icon } from './icons.js';
import { Sounds } from './sounds.js';
import {
  BackgroundEffects, EFFECTS, isWebGLAvailable, getEffectThumbnail,
  idbList, idbAdd, idbDelete, registerEffect,
  type EffectId, type Profile,
} from './effects.js';
import type { LobbyResult } from './lobby.js';

type Source = 'camera' | 'mic' | 'screen';
type Role = 'host' | 'member' | 'guest';

interface ProducerInfo {
  peerId: string;
  producerId: string;
  kind: MC.MediaKind;
  source: Source;
}

export interface RoomProps {
  signaling: LobbyResult['signaling'];
  joinResp: LobbyResult['joinResp'];
  devices: LobbyResult['devices'];
  prefs: LobbyResult['prefs'];
  roomLabel: string;
  micStream: MediaStream | null;
  camStream: MediaStream | null;
  wasGuest?: boolean;
  authToken: string;
  roomId: string;
  apiOrigin: string;
}

export interface EndSummary {
  startedAt: number;
  endedAt: number;
  peakPeerCount: number;
  myName: string;
  roomLabel: string;
}

interface Callbacks {
  onEnded: (summary: EndSummary) => void;
  onConnectionLost: () => void;
}

interface PeerSlot {
  id: string;
  name: string;
  role: Role;
  tile: HTMLDivElement;
  cameraStream: MediaStream;
  screenStream: MediaStream;
  consumers: Map<string, { consumer: MC.Consumer; source: Source }>;
  isSelf: boolean;
  hasScreen: boolean;
  hasHandUp: boolean;
}

const HAND_PREFIX = '[__ala_meet_hand:'; // chat-message backed hand-raise hack (no server change)

export async function mountRoom(root: HTMLElement, props: RoomProps, cb: Callbacks): Promise<void> {
  const sig = props.signaling;
  const device = new Device();
  const selfRole: Role = props.joinResp.role ?? 'guest';
  const selfId = props.joinResp.peerId;
  const selfName = computeSelfName(props);
  const roomLabel = props.roomLabel;
  const startedAt = Date.now();
  let peakPeerCount = 1;
  let drawerTab: 'chat' | 'people' | 'files' = 'chat';

  interface FileInfo {
    id: string;
    name: string;
    size: number;
    mimeType: string;
    uploaderPeerId: string;
    uploaderDisplayName: string;
    uploadedAt: number;
  }
  const files = new Map<string, FileInfo>();
  for (const f of props.joinResp.existingFiles ?? []) files.set(f.id, f);

  // ── Costruzione layout ──
  root.innerHTML = `
    <section class="room ${selfRole === 'host' ? 'is-host' : ''}" id="room">
      <header class="room-top">
        <div class="room-top-left">
          <a class="brand-mini-wrap" href="/" aria-label="ALA Meet">
            <img class="brand-mini" src="/ala-logo.jpg" alt="ALA" />
            <span class="brand-mini-text">ALA Meet</span>
          </a>
          <span class="room-divider" aria-hidden="true">·</span>
          <h1 id="room-title">${escape(roomLabel)}<small id="room-meta">…</small></h1>
        </div>
        <div class="room-top-info">
          <span class="tb-pill rec" id="rec-indicator" hidden><span class="dot"></span> REC</span>
          <span class="tb-pill"><svg class="icon"><use href="#icon-shield"/></svg> Cifrato</span>
          <span class="tb-pill net-good" id="net-indicator"><svg class="icon"><use href="#icon-wifi"/></svg> <span id="net-text">ottima</span></span>
        </div>
      </header>

      <div class="room-stage" data-drawer="closed" id="stage">
        <section class="grid" id="grid" data-count="1" data-mode="grid"></section>
        <aside class="drawer" id="drawer">
          <div class="drawer-tabs">
            <button class="drawer-tab active" data-tab="chat">Chat</button>
            <button class="drawer-tab" data-tab="people">Persone <span class="count" id="peer-count">1</span></button>
            <button class="drawer-tab" data-tab="files">File <span class="count" id="file-count">0</span></button>
            <button class="drawer-close" id="drawer-close" aria-label="Chiudi">${icon('x', 'icon icon-sm')}</button>
          </div>
          <div class="drawer-body">
            <section class="drawer-section active" id="sec-chat">
              <div id="chat-log" style="flex:1; display:flex; flex-direction:column; gap:14px;"></div>
            </section>
            <section class="drawer-section" id="sec-people"></section>
            <section class="drawer-section" id="sec-files">
              <div class="files-upload-row">
                <input type="file" id="file-input" hidden />
                <button type="button" class="files-upload-btn" id="files-upload-btn">
                  ${icon('paperclip', 'icon icon-sm')} <span>Carica file</span>
                </button>
                <div class="files-upload-hint">Max 50 MB · 20 file · 300 MB totali</div>
              </div>
              <div class="files-progress" id="files-progress" hidden></div>
              <div class="files-list" id="files-list"></div>
              <div class="files-empty" id="files-empty">Nessun file condiviso.</div>
            </section>
          </div>
          <form class="chat-input-row" id="chat-form">
            <div class="chat-target-row">
              <label class="chat-target-label" for="chat-target">A:</label>
              <select class="chat-target" id="chat-target" aria-label="Destinatario">
                <option value="">Tutti</option>
              </select>
            </div>
            <div class="chat-input-line">
              <input class="chat-input" id="chat-input" placeholder="Scrivi un messaggio..." autocomplete="off" />
              <button class="chat-send" type="submit" aria-label="Invia">${icon('send', 'icon icon-sm')}</button>
            </div>
          </form>
        </aside>
      </div>

      <footer class="controls">
        <button class="ctrl ${props.prefs.micOn ? '' : 'off'}" id="btn-mic"><span class="tooltip">Microfono (M)</span>${icon(props.prefs.micOn ? 'mic' : 'mic-off', 'icon icon-lg')}</button>
        <button class="ctrl ${props.prefs.camOn ? '' : 'off'}" id="btn-cam"><span class="tooltip">Camera (V)</span>${icon(props.prefs.camOn ? 'cam' : 'cam-off', 'icon icon-lg')}</button>
        <button class="ctrl" id="btn-screen"><span class="tooltip">Condividi schermo</span>${icon('screen', 'icon icon-lg')}</button>
        <button class="ctrl" id="btn-hand"><span class="tooltip">Alza la mano (R)</span>${icon('hand', 'icon icon-lg')}</button>
        <div class="ctrl-spacer"></div>
        <button class="ctrl" id="btn-chat"><span class="tooltip">Chat</span>${icon('chat', 'icon icon-lg')}<span class="ctrl-count chat-unread" id="ctrl-chat-unread" hidden>0</span></button>
        <button class="ctrl" id="btn-people"><span class="tooltip">Partecipanti</span>${icon('people', 'icon icon-lg')}<span class="ctrl-count" id="ctrl-people-count">1</span></button>
        <button class="ctrl" id="btn-files"><span class="tooltip">File condivisi</span>${icon('paperclip', 'icon icon-lg')}<span class="ctrl-count file-unread" id="ctrl-file-unread" hidden>0</span></button>
        <button class="ctrl host-only" id="btn-record"><span class="tooltip">Registra</span>${icon('record', 'icon icon-lg')}</button>
        <button class="ctrl host-only" id="btn-close-room"><span class="tooltip">Chiudi stanza</span>${icon('x', 'icon icon-lg')}</button>
        <button class="ctrl" id="btn-effects" hidden><span class="tooltip">Effetti sfondo</span>${icon('settings', 'icon icon-lg')}</button>
        <button class="ctrl" id="btn-sounds"><span class="tooltip">Suoni</span>${icon(Sounds.isOn() ? 'volume' : 'volume-off', 'icon icon-lg')}</button>
        <div class="ctrl-spacer"></div>
        <button class="ctrl danger" id="btn-leave"><span class="tooltip">Esci (Q)</span>${icon('hang-up', 'icon icon-lg')}</button>
      </footer>

      <div class="toasts" id="toasts"></div>
    </section>
  `;

  const grid = root.querySelector<HTMLElement>('#grid')!;
  const drawer = root.querySelector<HTMLElement>('#drawer')!;
  const stage = root.querySelector<HTMLElement>('#stage')!;
  const chatLog = root.querySelector<HTMLElement>('#chat-log')!;
  const chatInput = root.querySelector<HTMLInputElement>('#chat-input')!;
  const chatForm = root.querySelector<HTMLFormElement>('#chat-form')!;
  const chatTarget = root.querySelector<HTMLSelectElement>('#chat-target')!;
  const peopleSec = root.querySelector<HTMLElement>('#sec-people')!;
  const peerCountEl = root.querySelector<HTMLElement>('#peer-count')!;
  const recInd = root.querySelector<HTMLElement>('#rec-indicator')!;
  const netInd = root.querySelector<HTMLElement>('#net-indicator')!;
  const netText = root.querySelector<HTMLElement>('#net-text')!;
  const roomMeta = root.querySelector<HTMLElement>('#room-meta')!;
  const toasts = root.querySelector<HTMLElement>('#toasts')!;

  const btnMic = root.querySelector<HTMLButtonElement>('#btn-mic')!;
  const btnCam = root.querySelector<HTMLButtonElement>('#btn-cam')!;
  const btnScreen = root.querySelector<HTMLButtonElement>('#btn-screen')!;
  const btnHand = root.querySelector<HTMLButtonElement>('#btn-hand')!;
  const btnChat = root.querySelector<HTMLButtonElement>('#btn-chat')!;
  const ctrlChatUnread = root.querySelector<HTMLElement>('#ctrl-chat-unread')!;
  const btnPeople = root.querySelector<HTMLButtonElement>('#btn-people')!;
  const ctrlPeopleCount = root.querySelector<HTMLElement>('#ctrl-people-count')!;
  const btnFiles = root.querySelector<HTMLButtonElement>('#btn-files')!;
  const ctrlFileUnread = root.querySelector<HTMLElement>('#ctrl-file-unread')!;
  const fileCountEl = root.querySelector<HTMLElement>('#file-count')!;
  const filesList = root.querySelector<HTMLElement>('#files-list')!;
  const filesEmpty = root.querySelector<HTMLElement>('#files-empty')!;
  const fileInput = root.querySelector<HTMLInputElement>('#file-input')!;
  const filesUploadBtn = root.querySelector<HTMLButtonElement>('#files-upload-btn')!;
  const filesProgress = root.querySelector<HTMLElement>('#files-progress')!;
  const btnRecord = root.querySelector<HTMLButtonElement>('#btn-record')!;
  const btnCloseRoom = root.querySelector<HTMLButtonElement>('#btn-close-room')!;
  const btnSounds = root.querySelector<HTMLButtonElement>('#btn-sounds')!;
  const btnEffects = root.querySelector<HTMLButtonElement>('#btn-effects')!;
  const btnLeave = root.querySelector<HTMLButtonElement>('#btn-leave')!;

  // Timer
  const startTime = Date.now();
  function updateMeta() {
    const elapsedMs = Date.now() - startTime;
    const peerCount = peers.size;
    roomMeta.textContent = `${peerCount} partecipanti · ${formatDuration(elapsedMs)}`;
  }
  const metaTimer = window.setInterval(updateMeta, 1000);

  // ── State ──
  const peers = new Map<string, PeerSlot>();

  // Per i guest entrati direttamente, joinResp ha già routerRtpCapabilities.
  // Se sono entrato via waiting, joinResp.waiting era true → caps mancanti, richiedo ora.
  let routerCaps: MC.RtpCapabilities;
  if (props.wasGuest || !props.joinResp.routerRtpCapabilities) {
    const r = await sig.request<{ rtpCapabilities: MC.RtpCapabilities }>({ type: 'getRouterRtpCapabilities' });
    routerCaps = r.rtpCapabilities;
  } else {
    routerCaps = props.joinResp.routerRtpCapabilities;
  }
  await device.load({ routerRtpCapabilities: routerCaps });

  let iceServers: RTCIceServer[] = [];
  try {
    const turn = await sig.request<{ username: string; credential: string; urls: string[] }>({ type: 'getTurnCredentials' });
    if (turn?.urls?.length) iceServers = [{ urls: turn.urls, username: turn.username, credential: turn.credential }];
  } catch { /* */ }

  const sendTransport = await createSendTransport(sig, device, iceServers);
  const recvTransport = await createRecvTransport(sig, device, iceServers);

  // Self tile + local stream già pronto dalla lobby
  ensurePeer(selfId, selfName, selfRole, true);
  const selfSlot = peers.get(selfId)!;
  selfSlot.tile.classList.add('local');
  if (props.camStream) {
    const v = selfSlot.tile.querySelector<HTMLVideoElement>('video')!;
    v.srcObject = props.camStream;
    v.muted = true;
    selfSlot.tile.classList.toggle('cam-off', !props.prefs.camOn);
    // iOS: anche il video locale può non auto-partire senza un .play() esplicito
    void v.play().catch(() => {});
  } else {
    selfSlot.tile.classList.toggle('cam-off', true);
  }

  // Pre-popolo peer dalla joinResp
  for (const p of props.joinResp.peers ?? []) {
    ensurePeer(p.id, p.displayName, p.role, false).tile.classList.add('loading');
  }
  for (const p of props.joinResp.waitingGuests ?? []) {
    appendWaitingGuest(p.id, p.displayName);
  }
  if (props.joinResp.recording) showRec(props.joinResp.recording.startedBy);
  updatePeerCount();
  updateMeta();

  // Publish
  let audioProducer: MC.Producer | undefined;
  let videoProducer: MC.Producer | undefined;
  let screenProducer: MC.Producer | undefined;

  // ── State del watchdog frame-flow / keyframe (HOISTATO QUI per evitare TDZ:
  // le funzioni `verifyAndRecoverFrameFlow` / `startPeriodicFrameFlowWatchdog`
  // sono dichiarate piu' in basso ma vengono invocate gia' al primo produce
  // qui sotto. Le `let` non sono hoisted come le function declarations →
  // referenziarle dentro la closure prima della loro dichiarazione = TDZ. ──
  let frameFlowLock = false;
  let frameFlowInterval: ReturnType<typeof setInterval> | null = null;
  let lastFramesSent = 0;
  let consecutiveStalls = 0;
  /** Backoff fix 0.9.6-server fix2 (loop infinito test-keyframe-fix1): dopo
   *  MAX_RECOVERY tentativi senza miglioramento, disabilita il watchdog. Inutile
   *  ri-acquisire la camera ogni 8s se la metrica e' rotta o l'utente sta bene. */
  let recoveryAttempts = 0;
  const MAX_RECOVERY = 2;
  let watchdogDisabled = false;
  /** Second-signal di sanity: timestamp dell'ultimo frame renderizzato sul
   *  <video> self-view (via requestVideoFrameCallback). Se aggiornato di recente
   *  il video LOCALE scorre davvero → ignora "stall" in stats (i.e. lettura
   *  errata di simulcast layer non attivo). */
  let lastSelfFrameAt = 0;
  if (props.micStream && props.prefs.micOn) {
    const t = props.micStream.getAudioTracks()[0];
    if (t) audioProducer = await sendTransport.produce({ track: t, codecOptions: { opusStereo: true, opusDtx: true }, appData: { source: 'mic' } });
  }
  if (props.camStream && props.prefs.camOn) {
    const t = props.camStream.getVideoTracks()[0];
    if (t) videoProducer = await sendTransport.produce({
      track: t,
      encodings: cameraEncodings(),
      codecOptions: { videoGoogleStartBitrate: 1000 },
      appData: { source: 'camera' },
    });
    // FIX 0.9.6-server: dopo la prima pubblicazione video, alcuni browser
    // (Chrome/Safari mobile) pubblicano un MediaStreamTrack readyState='live'
    // ma senza frame in flusso → freeze "permanente" all'ingresso finche'
    // l'utente non fa toggle camera. Watchdog post-produce + periodico:
    //   - one-shot a 1.2s per intercettare il freeze iniziale
    //   - periodico ogni 3s per intercettare lo "stop dopo 1 frame" del mobile.
    if (videoProducer) {
      startSelfViewFrameTracker();
      void verifyAndRecoverFrameFlow('initial-produce');
      startPeriodicFrameFlowWatchdog();
    }
  }

  // Consume produttori preesistenti
  for (const p of props.joinResp.existingProducers ?? []) await consume(p).catch(() => {});

  // ── Notifiche server ──
  sig.on('peerJoined', (n) => {
    Sounds.join();
    pushToast(`<strong>${escape(n.displayName as string)} è entrato</strong><small>${roleLabel(n.role as Role)}</small>`, n.displayName as string);
    ensurePeer(n.peerId as string, n.displayName as string, (n.role as Role) ?? 'member', false).tile.classList.add('loading');
    if (peers.size > peakPeerCount) peakPeerCount = peers.size;
    updatePeerCount(); renderPeople();
  });
  sig.on('peerLeft', (n) => {
    Sounds.leave();
    const slot = peers.get(n.peerId as string);
    if (slot) {
      pushToast(`<strong>${escape(slot.name)} è uscito</strong>`, slot.name);
      slot.tile.remove();
      peers.delete(n.peerId as string);
    }
    updatePeerCount(); renderPeople();
  });
  sig.on('newProducer', (n) => { consume(n as unknown as ProducerInfo).catch(() => {}); });
  sig.on('producerClosed', (n) => {
    onProducerClosed(n.producerId as string);
  });
  sig.on('producerPaused', (n) => onProducerPaused(n.producerId as string, n.peerId as string, true));
  sig.on('producerResumed', (n) => onProducerPaused(n.producerId as string, n.peerId as string, false));
  sig.on('activeSpeaker', (n) => {
    const pid = (n.peerId as string | null) ?? null;
    for (const s of peers.values()) s.tile.classList.toggle('speaking', s.id === pid);
  });
  sig.on('chatMessage', (n) => onChat(
    n.peerId as string,
    n.displayName as string,
    n.text as string,
    n.ts as number,
    {
      private: Boolean(n.private),
      targetPeerId: n.targetPeerId as string | undefined,
      targetDisplayName: n.targetDisplayName as string | undefined,
    },
  ));
  sig.on('consumerLayersChanged', (n) => onLayers(n.consumerId as string, (n.spatialLayer as number | null) ?? null));
  sig.on('guestKnocking', (n) => {
    pushToast(`<strong>${escape(n.displayName as string)} chiede di entrare</strong><small>Sala d'attesa</small>`, n.displayName as string);
    appendWaitingGuest(n.peerId as string, n.displayName as string);
  });
  sig.on('guestWaitingCleared', (n) => {
    const li = peopleSec.querySelector(`[data-pending="${CSS.escape(n.peerId as string)}"]`);
    li?.remove();
  });
  sig.on('roomClosed', () => endCall('roomClosed'));
  sig.on('kicked', () => endCall('kicked'));
  sig.on('forcedMute', () => pushToast(`<strong>L'host ti ha silenziato</strong>`, ''));
  sig.on('recordingStarted', (n) => { Sounds.rec(); showRec(n.startedBy as string); });
  sig.on('recordingStopped', () => hideRec());

  let endedReason: 'local' | 'roomClosed' | 'kicked' | null = null;
  sig.onClose(() => {
    // Se l'utente o un evento server hanno già chiuso intenzionalmente,
    // non interpretare la chiusura WS come "connessione persa".
    if (endedReason) return;
    cleanup();
    cb.onConnectionLost();
  });

  // ── Controlli ──
  btnMic.addEventListener('click', toggleMic);
  btnCam.addEventListener('click', toggleCam);
  btnScreen.addEventListener('click', toggleScreen);
  btnHand.addEventListener('click', toggleHand);
  btnChat.addEventListener('click', () => {
    const isOpenOnChat = stage.dataset.drawer === 'open' && drawerTab === 'chat';
    if (isOpenOnChat) toggleDrawer(false);
    else { switchTab('chat'); toggleDrawer(true); }
  });
  btnPeople.addEventListener('click', () => {
    const isOpenOnPeople = stage.dataset.drawer === 'open' && drawerTab === 'people';
    if (isOpenOnPeople) toggleDrawer(false);
    else { switchTab('people'); toggleDrawer(true); }
  });
  btnFiles.addEventListener('click', () => {
    const isOpenOnFiles = stage.dataset.drawer === 'open' && drawerTab === 'files';
    if (isOpenOnFiles) toggleDrawer(false);
    else { switchTab('files'); toggleDrawer(true); }
  });
  btnRecord.addEventListener('click', toggleRec);
  btnCloseRoom.addEventListener('click', async () => {
    if (await ask('Chiudere la stanza per tutti?', 'Chiudi stanza', 'Annulla', true)) {
      sig.request({ type: 'closeRoom' }).catch((e) => {
        toastError('Chiusura fallita', (e as Error).message, '');
      });
    }
  });
  btnSounds.addEventListener('click', () => {
    const on = Sounds.toggle();
    btnSounds.innerHTML = `<span class="tooltip">Suoni</span>${icon(on ? 'volume' : 'volume-off', 'icon icon-lg')}`;
  });

  // ── Sfondi virtuali (effetti) ──
  let effects: BackgroundEffects | null = null;
  let currentEffect: EffectId = (localStorage.getItem('ala-meet.effect') as EffectId | null) ?? 'none';
  let currentProfile: Profile = (localStorage.getItem('ala-meet.effect.profile') as Profile | null) ?? 'auto';
  const supportsEffects = isWebGLAvailable() && typeof HTMLCanvasElement.prototype.captureStream === 'function';
  if (supportsEffects) btnEffects.hidden = false;
  btnEffects.addEventListener('click', () => void openEffectsModal());

  async function openEffectsModal(): Promise<void> {
    const isMobile = matchMedia('(hover: none) and (pointer: coarse)').matches;
    const warnHTML = isMobile
      ? '<div class="effects-warn">⚠ Su mobile gli effetti consumano CPU e batteria. Profilo "Eco" è attivo per default.</div>'
      : '';
    const customs = await idbList();
    customs.forEach((c) => registerEffect({ id: c.id, label: c.name, description: 'Sfondo personalizzato', kind: 'image' }));
    const overlay = document.createElement('div');
    overlay.className = 'ala-modal-overlay';
    overlay.innerHTML = `
      <div class="ala-modal-card effects-modal">
        <h3 class="effects-title">Sfondo virtuale</h3>
        ${warnHTML}
        <div class="effects-profile">
          <span class="effects-profile-label">Qualità:</span>
          <label><input type="radio" name="eff-profile" value="auto" ${currentProfile === 'auto' ? 'checked' : ''}> Auto</label>
          <label><input type="radio" name="eff-profile" value="eco" ${currentProfile === 'eco' ? 'checked' : ''}> Eco</label>
          <label><input type="radio" name="eff-profile" value="alto" ${currentProfile === 'alto' ? 'checked' : ''}> Alto</label>
          <span class="effects-profile-info" id="eff-profile-info">${effects ? `attivo: ${effects.getResolvedProfile()}` : '—'}</span>
        </div>
        <div class="effects-grid" id="effects-grid">
          ${EFFECTS.map((e) => {
            const thumbUrl = e.imageUrl ?? getEffectThumbnail(e.id);
            const style = thumbUrl ? `style="background-image:url('${thumbUrl}')"` : '';
            return `
            <button type="button" class="effect-card ${e.id === currentEffect ? 'selected' : ''}" data-effect="${e.id}" ${e.attribution ? `title="${escape(e.attribution)}"` : ''}>
              <div class="effect-thumb effect-thumb-${e.id} ${thumbUrl ? 'effect-thumb-rendered' : ''}" ${style}></div>
              <div class="effect-label">${escape(e.label)}</div>
              <div class="effect-desc">${escape(e.description)}</div>
              ${e.id.startsWith('custom-') ? `<button class="effect-delete" data-del="${e.id}" title="Rimuovi">${icon('x', 'icon icon-sm')}</button>` : ''}
            </button>`;
          }).join('')}
          <label class="effect-card effect-upload">
            <input type="file" accept="image/*" id="eff-upload" hidden>
            <div class="effect-thumb effect-thumb-upload">+</div>
            <div class="effect-label">Carica…</div>
            <div class="effect-desc">Una tua immagine (resta sul dispositivo).</div>
          </label>
        </div>
        <div class="ala-modal-actions">
          <button type="button" class="ala-modal-cancel">Chiudi</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);
    const close = () => overlay.remove();
    overlay.querySelector<HTMLButtonElement>('.ala-modal-cancel')!.addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
    overlay.querySelectorAll<HTMLInputElement>('input[name=eff-profile]').forEach((r) => {
      r.addEventListener('change', async () => {
        const p = r.value as Profile;
        currentProfile = p;
        localStorage.setItem('ala-meet.effect.profile', p);
        if (effects) {
          await effects.setProfile(p);
          const info = overlay.querySelector('#eff-profile-info');
          if (info) info.textContent = `attivo: ${effects.getResolvedProfile()}`;
        }
      });
    });
    overlay.querySelectorAll<HTMLButtonElement>('[data-effect]').forEach((card) => {
      card.addEventListener('click', async (ev) => {
        if ((ev.target as HTMLElement).closest('[data-del]')) return;
        const id = card.dataset.effect as EffectId;
        overlay.querySelectorAll('.effect-card').forEach((c) => c.classList.toggle('selected', c === card));
        await applyEffect(id);
      });
    });
    overlay.querySelectorAll<HTMLButtonElement>('[data-del]').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const id = btn.dataset.del!;
        if (await ask('Rimuovere questo sfondo personalizzato?', 'Rimuovi', 'Annulla', true)) {
          await idbDelete(id);
          if (currentEffect === id) await applyEffect('none');
          close();
        }
      });
    });
    const upload = overlay.querySelector<HTMLInputElement>('#eff-upload');
    upload?.addEventListener('change', async () => {
      const file = upload.files?.[0];
      if (!file) return;
      if (!/^image\//.test(file.type)) {
        toastError('Formato non supportato', 'Carica una immagine (jpg/png/webp).', '');
        return;
      }
      const rec = await idbAdd(file.name, file);
      registerEffect({ id: rec.id, label: file.name, description: 'Sfondo personalizzato', kind: 'image' });
      await applyEffect(rec.id);
      close();
    });
  }

  /**
   * Lock per serializzare gli applyEffect: click rapidi in sequenza nel modale
   * causavano race (es. effects.init recreating segmenter mentre il render
   * precedente era ancora in volo → "track ended" sul canvas captureStream
   * quando l'iterazione precedente provava a replaceTrack).
   */
  let effectsLock: Promise<void> = Promise.resolve();
  let effectsRequestSeq = 0;

  async function applyEffect(id: EffectId): Promise<void> {
    if (!supportsEffects) {
      pushToast(`<strong>Effetti non supportati</strong><small>Browser senza WebGL/captureStream</small>`, selfName);
      return;
    }
    const reqSeq = ++effectsRequestSeq;
    currentEffect = id;
    localStorage.setItem('ala-meet.effect', id);
    btnEffects.classList.toggle('active', id !== 'none');
    // Self-view: niente mirror CSS quando un effetto è attivo (stile Meet) —
    // altrimenti lo sfondo dipinto (logo ALA, testi) si vede al contrario.
    selfSlot.tile.classList.toggle('has-effect', id !== 'none');
    if (!videoProducer) return;

    // Serializza: aspetta che l'eventuale applyEffect precedente sia terminata.
    const prev = effectsLock;
    let release: () => void = () => {};
    effectsLock = new Promise<void>((r) => { release = r; });
    try {
      await prev;
    } catch { /* errori della precedente non bloccano la nuova */ }

    try {
      // Se nel frattempo è stato richiesto un nuovo effetto, scarta questo
      // (latest-wins): inutile applicare A se mentre eseguivamo il lock è
      // arrivata la richiesta per B.
      if (reqSeq !== effectsRequestSeq) return;

      const live = await ensureLiveCamStream();
      if (!live) throw new Error('camera non disponibile');
      const rawTrack = live.getVideoTracks()[0];
      if (!rawTrack || rawTrack.readyState !== 'live') throw new Error('camera non disponibile');

      if (id === 'none') {
        await videoProducer.replaceTrack({ track: rawTrack });
        const v = selfSlot.tile.querySelector<HTMLVideoElement>('video')!;
        v.srcObject = live;
        safePlay(v);
        void verifyAndRecoverFrameFlow('apply-effect-none');
        return;
      }

      if (!effects) {
        effects = new BackgroundEffects();
        effects.setOnPerformanceDrop(() => {
          pushToast(`<strong>Profilo Eco attivato</strong><small>Calcolo troppo lento per la qualità Alta</small>`, selfName);
        });
      }
      await effects.init(currentProfile);
      if (reqSeq !== effectsRequestSeq) return; // re-check dopo await async
      await effects.setInputStream(live);
      if (reqSeq !== effectsRequestSeq) return;
      await effects.setEffect(id);
      if (reqSeq !== effectsRequestSeq) return;
      // FIX freeze 0.9.5-server: garantisce che il canvas abbia gia' disegnato
      // almeno un frame PRIMA di pubblicare la track al producer mediasoup.
      // Senza questo, replaceTrack riceve una track captureStream "vuota" che
      // resta vista come ultimo frame congelato lato peer remoti finche'
      // l'utente non disattiva/riattiva la camera.
      await effects.primeOutput();
      if (reqSeq !== effectsRequestSeq) return;

      const out = effects.getOutputStream();
      const newTrack = out.getVideoTracks()[0];
      if (!newTrack || newTrack.readyState !== 'live') throw new Error('output track non disponibile');
      await videoProducer.replaceTrack({ track: newTrack });
      const v = selfSlot.tile.querySelector<HTMLVideoElement>('video')!;
      v.srcObject = out;
      safePlay(v);
      // Watchdog post-replaceTrack: verifica che frame escano davvero E che il
      // canvas non emetta nero (bug 13-06 desktop sfondo "Sfocato" lum=0).
      void verifyAndRecoverFrameFlow('apply-effect-' + id);
    } catch (e) {
      toastError('Effetto non applicato', (e as Error).message, selfName);
      currentEffect = 'none';
      localStorage.setItem('ala-meet.effect', 'none');
      btnEffects.classList.remove('active');
      selfSlot.tile.classList.remove('has-effect');
    } finally {
      release();
    }
  }

  // Se l'utente aveva un effetto persistito, riapplica all'avvio della stanza
  // (deve avvenire dopo la creazione del videoProducer iniziale).
  if (supportsEffects && currentEffect !== 'none') {
    queueMicrotask(() => { void applyEffect(currentEffect); });
  }
  btnLeave.addEventListener('click', () => endCall('local'));

  // Marker per i test E2E: tutti i listener dei controlli sono attaccati.
  root.querySelector('.controls')!.setAttribute('data-ready', '1');

  root.querySelector('#drawer-close')!.addEventListener('click', () => toggleDrawer(false));
  root.querySelectorAll<HTMLButtonElement>('.drawer-tab[data-tab]').forEach((tab) => {
    tab.addEventListener('click', () => switchTab(tab.dataset.tab as 'chat' | 'people' | 'files'));
  });

  chatTarget.addEventListener('change', () => {
    chatTarget.classList.toggle('is-private', !!chatTarget.value);
    const others = [...peers.values()].filter((p) => !p.isSelf);
    chatInput.placeholder = chatTarget.value
      ? `Messaggio privato a ${others.find((p) => p.id === chatTarget.value)?.name ?? 'destinatario'}…`
      : 'Scrivi un messaggio...';
    chatInput.focus();
  });

  chatForm.addEventListener('submit', (e) => {
    e.preventDefault();
    const text = chatInput.value.trim();
    if (!text) return;
    if (text.startsWith(HAND_PREFIX)) return; // utente non può triggerare hand-prefix
    const targetPeerId = chatTarget.value || undefined;
    chatInput.value = '';
    const payload: { type: 'chatMessage'; text: string; targetPeerId?: string } = { type: 'chatMessage', text };
    if (targetPeerId) payload.targetPeerId = targetPeerId;
    sig.request(payload).catch((e) => {
      toastError('Messaggio non inviato', (e as Error).message, '');
    });
  });

  // Shortcuts tastiera
  window.addEventListener('keydown', (e) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    if (e.key === 'm' || e.key === 'M') { e.preventDefault(); toggleMic(); }
    if (e.key === 'v' || e.key === 'V') { e.preventDefault(); toggleCam(); }
    if (e.key === 'r' || e.key === 'R') { e.preventDefault(); toggleHand(); }
    if (e.key === 'q' || e.key === 'Q') { e.preventDefault(); endCall('local'); }
  });

  // Network indicator polling — semplice
  const netTimer = window.setInterval(updateNetwork, 5000);
  function updateNetwork() {
    // semplice: derivato dallo stato della WS
    if (!sig.isOpen()) {
      netInd.className = 'tb-pill net-bad';
      netText.textContent = 'persa';
      return;
    }
    netInd.className = 'tb-pill net-good';
    netText.textContent = 'ottima';
  }

  // ── Funzioni ──
  function ensurePeer(peerId: string, name: string, role: Role, isSelf: boolean): PeerSlot {
    let slot = peers.get(peerId);
    if (slot) return slot;
    const tile = document.createElement('div');
    tile.className = 'tile';
    tile.dataset.peerId = peerId;
    tile.dataset.source = 'camera'; // default; cambia a 'screen' in enterPresenter
    const initial = initials(name);
    // iOS Safari: <video> con audio non-muted bloccato finché non c'è user gesture.
    // Tutti i tile partono muted; al primo tap globale facciamo unmute dei remoti.
    tile.innerHTML = `
      <video autoplay playsinline muted></video>
      <div class="tile-face" style="background: ${avatarGradient(peerId)}; color: var(--ala-gold);">${escape(initial)}</div>
      ${selfRole === 'host' && !isSelf && role !== 'host' ? `
        <div class="peer-actions">
          <button data-act="mute" title="Silenzia">${icon('mic-off', 'icon icon-sm')}</button>
          <button data-act="kick" title="Espelli" class="danger">${icon('x', 'icon icon-sm')}</button>
        </div>
      ` : ''}
      <div class="tile-quality">
        <span class="qbar"></span><span class="qbar"></span><span class="qbar"></span><span class="qbar"></span>
      </div>
      <div class="tile-badges"></div>
      <div class="tile-label ${role === 'host' ? 'host' : ''}">${isSelf ? '<span class="you-dot"></span>' : ''}${escape(name)}${role === 'host' ? ' · host' : ''}${isSelf ? ' (tu)' : ''}</div>
    `;
    grid.appendChild(tile);

    if (selfRole === 'host' && !isSelf && role !== 'host') {
      tile.querySelector('[data-act=mute]')!.addEventListener('click', () => {
        sig.request({ type: 'mutePeer', peerId }).catch(() => {});
      });
      tile.querySelector('[data-act=kick]')!.addEventListener('click', async () => {
        if (await ask(`Espellere ${name}?`, 'Espelli', 'Annulla', true)) {
          sig.request({ type: 'kickPeer', peerId, ban: false }).catch((e) => {
            toastError('Espulsione fallita', (e as Error).message, name);
          });
        }
      });
    }

    slot = {
      id: peerId, name, role, tile,
      cameraStream: new MediaStream(),
      screenStream: new MediaStream(),
      consumers: new Map(), isSelf, hasScreen: false, hasHandUp: false,
    };
    peers.set(peerId, slot);
    grid.dataset.count = String(peers.size);
    return slot;
  }

  function updatePeerCount() {
    peerCountEl.textContent = String(peers.size);
    ctrlPeopleCount.textContent = String(peers.size);
    grid.dataset.count = String(peers.size);
    updateMeta();
    updateChatTargetOptions();
  }
  function updateChatTargetOptions() {
    const current = chatTarget.value;
    const others = [...peers.values()].filter((p) => !p.isSelf);
    const opts = ['<option value="">Tutti</option>']
      .concat(others.map((p) => `<option value="${escape(p.id)}">Privato a ${escape(p.name)}</option>`));
    chatTarget.innerHTML = opts.join('');
    // Mantieni la selezione se ancora valida, altrimenti torna a "Tutti".
    if (current && others.some((p) => p.id === current)) chatTarget.value = current;
    else chatTarget.value = '';
    chatTarget.classList.toggle('is-private', !!chatTarget.value);
    chatInput.placeholder = chatTarget.value
      ? `Messaggio privato a ${others.find((p) => p.id === chatTarget.value)?.name ?? 'destinatario'}…`
      : 'Scrivi un messaggio...';
  }

  async function consume(info: ProducerInfo) {
    if (info.peerId === selfId) return;
    const slot = ensurePeer(info.peerId, info.peerId.slice(0, 6), 'member', false);
    const data = await sig.request<{ id: string; producerId: string; kind: MC.MediaKind; rtpParameters: MC.RtpParameters }>({
      type: 'consume', producerId: info.producerId, transportId: recvTransport.id,
      rtpCapabilities: device.rtpCapabilities,
    });
    const consumer = await recvTransport.consume({
      id: data.id, producerId: data.producerId, kind: data.kind, rtpParameters: data.rtpParameters,
    });
    await sig.request({ type: 'resumeConsumer', consumerId: consumer.id });
    slot.consumers.set(consumer.id, { consumer, source: info.source });
    slot.tile.classList.remove('loading');

    if (info.source === 'screen') {
      slot.screenStream.addTrack(consumer.track);
      slot.hasScreen = true;
      enterPresenter(info.peerId);
    } else {
      slot.cameraStream.addTrack(consumer.track);
      const v = slot.tile.querySelector<HTMLVideoElement>('video')!;
      if (!v.srcObject || info.source === 'camera') {
        v.srcObject = slot.cameraStream;
        safePlay(v);
      }
      // se l'utente ha già fatto un gesture, applica subito unmute al nuovo arrivato
      if (audioUnlocked) { v.muted = false; safePlay(v); }
      if (info.source === 'camera') {
        // Setta layer iniziale in base a dimensione tile attuale + osserva resize.
        updatePreferredLayer(consumer.id, slot.tile);
        observeTileSize(slot.tile, consumer.id);
        // FIX 0.9.6-server (dato decisivo 13-06): se il PUBLISHER REMOTO
        // si blocca dopo il primo frame (caso mobile osservato dall'host),
        // il <video> remoto mostra currentTime fermo. Watchdog di stallo
        // → RPC requestKeyFrame al server, che chiede al producer un nuovo
        // keyframe (mediasoup consumer.requestKeyFrame → PLI verso il publisher).
        watchConsumerStall(consumer, slot.tile.querySelector<HTMLVideoElement>('video')!);
      }
    }
  }

  /**
   * Monitora il currentTime del <video> remoto: se non avanza per >4s mentre
   * il consumer non e' in pausa, segnala lo stallo al server per richiedere
   * un keyframe nuovo al producer mobile. Idempotente; max 1 richiesta ogni 5s
   * per non spammare il publisher in caso di stallo persistente.
   */
  function watchConsumerStall(consumer: MC.Consumer, videoEl: HTMLVideoElement): void {
    let lastCurrentTime = -1;
    let stallCount = 0;
    let lastRequestAt = 0;
    const iv = setInterval(() => {
      if (consumer.closed) { clearInterval(iv); return; }
      if (consumer.paused || consumer.producerPaused) {
        stallCount = 0;
        lastCurrentTime = videoEl.currentTime;
        return;
      }
      const ct = videoEl.currentTime;
      if (ct === lastCurrentTime) {
        stallCount++;
        // 2 check consecutivi a 2s = 4s di stallo reale.
        if (stallCount >= 2 && Date.now() - lastRequestAt > 5000) {
          lastRequestAt = Date.now();
          console.warn(`[room] consumer ${consumer.id.slice(0,8)} stallo ${ct}s → requestKeyFrame`);
          sig.request({ type: 'requestKeyFrame', consumerId: consumer.id })
            .catch((e) => console.warn('[room] requestKeyFrame failed', e));
          stallCount = 0;
        }
      } else {
        stallCount = 0;
      }
      lastCurrentTime = ct;
    }, 2000);
    consumer.observer.on('close', () => clearInterval(iv));
  }

  // ── Simulcast layer chooser: basato su larghezza tile ─────────────────
  // Spatial layer attuale per consumer (per evitare richieste duplicate).
  const currentSpatial = new Map<string, number>();
  function spatialForWidth(px: number): number {
    if (px < 280) return 0;       // bassa: < 280px → r0 (~ 320x180 a 100k)
    if (px < 560) return 1;       // media: < 560px → r1 (~ 640x360 a 300k)
    return 2;                      // alta: ≥ 560px → r2 (~ 1280x720 a 900k)
  }
  function updatePreferredLayer(consumerId: string, tileEl: HTMLElement) {
    const w = tileEl.getBoundingClientRect().width;
    if (w === 0) return;
    const spatial = spatialForWidth(w);
    if (currentSpatial.get(consumerId) === spatial) return;
    currentSpatial.set(consumerId, spatial);
    sig.request({ type: 'setConsumerPreferredLayers', consumerId, spatialLayer: spatial, temporalLayer: 2 })
      .catch((e) => console.warn('[room] setPreferredLayers failed', e));
  }
  function observeTileSize(tileEl: HTMLElement, consumerId: string) {
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect.width;
        if (w > 0) {
          const spatial = spatialForWidth(w);
          if (currentSpatial.get(consumerId) !== spatial) updatePreferredLayer(consumerId, tileEl);
        }
      }
    });
    ro.observe(tileEl);
  }

  function onProducerClosed(producerId: string) {
    for (const slot of peers.values()) {
      for (const [cid, info] of slot.consumers) {
        if (info.consumer.producerId === producerId) {
          info.consumer.close();
          slot.consumers.delete(cid);
          if (info.source === 'screen') {
            slot.hasScreen = false;
            exitPresenter();
          }
        }
      }
    }
  }
  function onProducerPaused(producerId: string, peerId: string, paused: boolean) {
    for (const slot of peers.values()) {
      if (slot.id !== peerId) continue;
      for (const info of slot.consumers.values()) {
        if (info.consumer.producerId !== producerId) continue;
        if (info.source === 'mic') updateBadge(slot, 'muted', paused);
        if (info.source === 'camera') slot.tile.classList.toggle('cam-off', paused);
      }
    }
  }
  function onLayers(consumerId: string, spatial: number | null) {
    for (const slot of peers.values()) {
      const info = slot.consumers.get(consumerId);
      if (!info) continue;
      if (info.source !== 'camera') return;
      const bars = slot.tile.querySelectorAll<HTMLSpanElement>('.tile-quality .qbar');
      const active = spatial === null ? 0 : spatial + 1;
      bars.forEach((b, i) => b.classList.toggle('on', i < active));
    }
  }
  function updateBadge(slot: PeerSlot, kind: 'muted' | 'hand', enabled: boolean) {
    const badges = slot.tile.querySelector<HTMLElement>('.tile-badges')!;
    let badge = badges.querySelector<HTMLElement>(`[data-badge="${kind}"]`);
    if (enabled) {
      if (!badge) {
        badge = document.createElement('div');
        badge.dataset.badge = kind;
        badge.className = `badge ${kind}`;
        badge.innerHTML = icon(kind === 'muted' ? 'mic-off' : 'hand', 'icon icon-sm');
        badges.appendChild(badge);
      }
    } else if (badge) {
      badge.remove();
    }
  }

  function enterPresenter(peerId: string) {
    grid.dataset.mode = 'presenter';
    for (const slot of peers.values()) slot.tile.classList.toggle('presenter', slot.id === peerId);
    const presented = peers.get(peerId);
    if (presented) {
      const v = presented.tile.querySelector<HTMLVideoElement>('video')!;
      v.srcObject = presented.screenStream;
      // Marker per il CSS: niente mirror sul self quando mostra screen, e usa
      // object-fit: contain (letterbox) per non tagliare contenuto condiviso.
      presented.tile.dataset.source = 'screen';
      safePlay(v);
    }
  }
  function exitPresenter() {
    grid.dataset.mode = 'grid';
    for (const slot of peers.values()) {
      slot.tile.classList.remove('presenter');
      const v = slot.tile.querySelector<HTMLVideoElement>('video')!;
      if (slot.isSelf) v.srcObject = props.camStream;
      else v.srcObject = slot.cameraStream;
      slot.tile.dataset.source = 'camera';
      safePlay(v);
    }
  }

  // ── Drawer ──
  function toggleDrawer(force?: boolean) {
    const open = force ?? stage.dataset.drawer !== 'open';
    stage.dataset.drawer = open ? 'open' : 'closed';
    btnChat.classList.toggle('active', open && drawerTab === 'chat');
    btnPeople.classList.toggle('active', open && drawerTab === 'people');
    btnFiles.classList.toggle('active', open && drawerTab === 'files');
  }
  function switchTab(tab: 'chat' | 'people' | 'files') {
    drawerTab = tab;
    root.querySelectorAll<HTMLButtonElement>('.drawer-tab[data-tab]').forEach((b) => b.classList.toggle('active', b.dataset.tab === tab));
    root.querySelectorAll<HTMLElement>('.drawer-section').forEach((s) => s.classList.toggle('active', s.id === `sec-${tab}`));
    if (tab === 'people') renderPeople();
    if (tab === 'files') renderFiles();
    const inputRow = root.querySelector<HTMLElement>('.chat-input-row');
    if (inputRow) inputRow.style.display = tab === 'chat' ? 'flex' : 'none';
    btnChat.classList.toggle('active', stage.dataset.drawer === 'open' && tab === 'chat');
    btnPeople.classList.toggle('active', stage.dataset.drawer === 'open' && tab === 'people');
    btnFiles.classList.toggle('active', stage.dataset.drawer === 'open' && tab === 'files');
    // L'intent dell'utente che clicca la tab è "ho visto" → azzera l'unread.
    // (Non gateare sullo stato del drawer: switchTab viene tipicamente invocato
    // PRIMA di toggleDrawer(true), quindi la guard precedente non scattava mai.)
    if (tab === 'chat') clearUnread();
    if (tab === 'files') clearFileUnread();
  }

  // ── Unread counter chat ──
  let unreadChat = 0;
  function isChatVisible(): boolean {
    return stage.dataset.drawer === 'open' && drawerTab === 'chat';
  }
  function bumpUnread() {
    if (isChatVisible()) return;
    unreadChat++;
    ctrlChatUnread.textContent = unreadChat > 99 ? '99+' : String(unreadChat);
    ctrlChatUnread.hidden = false;
    btnChat.classList.add('has-unread');
  }
  function clearUnread() {
    unreadChat = 0;
    ctrlChatUnread.hidden = true;
    btnChat.classList.remove('has-unread');
  }

  // ── Files (allegati) ──
  let unreadFiles = 0;
  function isFilesVisible(): boolean {
    return stage.dataset.drawer === 'open' && drawerTab === 'files';
  }
  function bumpFileUnread() {
    if (isFilesVisible()) return;
    unreadFiles++;
    ctrlFileUnread.textContent = unreadFiles > 99 ? '99+' : String(unreadFiles);
    ctrlFileUnread.hidden = false;
    btnFiles.classList.add('has-unread');
  }
  function clearFileUnread() {
    unreadFiles = 0;
    ctrlFileUnread.hidden = true;
    btnFiles.classList.remove('has-unread');
  }
  function fmtSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }
  function fmtTime(ts: number): string {
    const d = new Date(ts);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  function renderFiles() {
    fileCountEl.textContent = String(files.size);
    if (files.size === 0) {
      filesList.innerHTML = '';
      filesEmpty.hidden = false;
      return;
    }
    filesEmpty.hidden = true;
    const sorted = [...files.values()].sort((a, b) => a.uploadedAt - b.uploadedAt);
    filesList.innerHTML = sorted.map((f) => {
      const isMine = f.uploaderPeerId === selfId;
      const canDelete = isMine || selfRole === 'host';
      return `
        <div class="file-row" data-file-id="${escape(f.id)}">
          <div class="file-icon">${icon('file', 'icon icon-md')}</div>
          <div class="file-meta">
            <div class="file-name" title="${escape(f.name)}">${escape(f.name)}</div>
            <div class="file-info">
              <span>${escape(f.uploaderDisplayName)}</span>
              <span class="dot">·</span>
              <span>${fmtSize(f.size)}</span>
              <span class="dot">·</span>
              <span>${fmtTime(f.uploadedAt)}</span>
            </div>
          </div>
          <div class="file-actions">
            <button type="button" class="file-act" data-act="download" title="Scarica">${icon('download', 'icon icon-sm')}</button>
            ${canDelete ? `<button type="button" class="file-act danger" data-act="delete" title="Elimina">${icon('trash', 'icon icon-sm')}</button>` : ''}
          </div>
        </div>
      `;
    }).join('');
    filesList.querySelectorAll<HTMLButtonElement>('[data-act="download"]').forEach((b) => {
      b.addEventListener('click', () => {
        const row = b.closest('.file-row') as HTMLElement;
        downloadFile(row.dataset.fileId!);
      });
    });
    filesList.querySelectorAll<HTMLButtonElement>('[data-act="delete"]').forEach((b) => {
      b.addEventListener('click', async () => {
        const row = b.closest('.file-row') as HTMLElement;
        const fid = row.dataset.fileId!;
        const f = files.get(fid);
        if (!f) return;
        if (await ask(`Eliminare "${f.name}"?`, 'Elimina', 'Annulla', true)) {
          sig.request({ type: 'deleteAttachment', fileId: fid }).catch((e) => {
            toastError('Eliminazione fallita', (e as Error).message, '');
          });
        }
      });
    });
  }
  function updateFileCounters() {
    fileCountEl.textContent = String(files.size);
  }
  async function downloadFile(fileId: string) {
    const f = files.get(fileId);
    if (!f) return;
    try {
      const r = await sig.request<{ url: string }>({ type: 'getAttachmentUrl', fileId });
      const a = document.createElement('a');
      a.href = r.url;
      a.download = f.name;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toastError('Download fallito', (e as Error).message, '');
    }
  }
  // Upload
  filesUploadBtn.addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', async () => {
    const file = fileInput.files?.[0];
    fileInput.value = '';
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toastError('File troppo grande', 'Limite 50 MB per file.', '');
      return;
    }
    await uploadFile(file);
  });
  async function uploadFile(file: File): Promise<void> {
    // Server-side: il token JWT viene riverificato (firma+room+exp). NON è single_use
    // qui (single_use è già scattato al join WS), quindi può essere riusato per upload
    // finché l'exp non scade. Il peerId va in querystring per il cross-check con
    // la mappa activePeers tenuta dal modulo attachments lato server.
    const params = new URLSearchParams({
      roomId: props.roomId,
      peerId: selfId,
      token: props.authToken,
    });
    const url = `${props.apiOrigin}/files/upload?${params.toString()}`;
    filesProgress.hidden = false;
    filesProgress.innerHTML = `<div class="files-progress-label">${escape(file.name)} · <span id="up-pct">0%</span></div><div class="files-progress-bar"><div class="files-progress-fill" id="up-fill" style="width:0%"></div></div>`;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url);
        xhr.upload.onprogress = (ev) => {
          if (!ev.lengthComputable) return;
          const pct = Math.round((ev.loaded / ev.total) * 100);
          const fill = filesProgress.querySelector<HTMLElement>('#up-fill');
          const lbl = filesProgress.querySelector<HTMLElement>('#up-pct');
          if (fill) fill.style.width = `${pct}%`;
          if (lbl) lbl.textContent = `${pct}%`;
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`HTTP ${xhr.status}: ${xhr.responseText}`));
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(fd);
      });
    } catch (e) {
      toastError('Upload fallito', (e as Error).message, '');
    } finally {
      filesProgress.hidden = true;
      filesProgress.innerHTML = '';
    }
  }
  sig.on('fileShared', (n) => {
    const f = (n as unknown as { file: FileInfo }).file;
    if (!f) return;
    files.set(f.id, f);
    if (f.uploaderPeerId !== selfId) {
      bumpFileUnread();
      pushToast(`<strong>${escape(f.uploaderDisplayName)} ha condiviso un file</strong><small>${escape(f.name)}</small>`, f.uploaderDisplayName);
      Sounds.join();
    }
    updateFileCounters();
    if (isFilesVisible()) renderFiles();
  });
  sig.on('fileRemoved', (n) => {
    const fid = n.fileId as string;
    files.delete(fid);
    updateFileCounters();
    if (isFilesVisible()) renderFiles();
  });
  updateFileCounters();

  function onChat(
    peerId: string,
    displayName: string,
    text: string,
    ts: number,
    opts?: { private?: boolean; targetPeerId?: string; targetDisplayName?: string },
  ) {
    // hand raise/down embedded in chat
    const handMatch = text.match(/^\[__ala_meet_hand:(up|down)\]$/);
    if (handMatch) {
      const slot = peers.get(peerId);
      if (slot) {
        slot.hasHandUp = handMatch[1] === 'up';
        updateBadge(slot, 'hand', slot.hasHandUp);
        if (slot.hasHandUp) pushToast(`<strong>${escape(displayName)} alza la mano</strong>`, displayName);
      }
      return;
    }
    if (peerId !== selfId) bumpUnread();
    const isPrivate = !!opts?.private;
    const isSelfMsg = peerId === selfId;
    const row = document.createElement('div');
    row.className = 'msg' + (isSelfMsg ? ' self' : '') + (isPrivate ? ' private' : '');
    const date = new Date(ts);
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    // Badge privato: "Privato → Mario" sui miei in uscita / "Privato da Carla" su quelli ricevuti.
    let privateBadge = '';
    if (isPrivate) {
      const tgtName = opts?.targetDisplayName ?? 'destinatario';
      privateBadge = isSelfMsg
        ? `<span class="msg-private-badge">${icon('shield', 'icon icon-sm')} Privato → ${escape(tgtName)}</span>`
        : `<span class="msg-private-badge">${icon('shield', 'icon icon-sm')} Privato da ${escape(displayName)}</span>`;
    }
    row.innerHTML = `
      <div class="who"><span class="avatar-sm" style="background:${avatarGradient(peerId)}; color: var(--ala-gold);">${escape(initials(displayName))}</span> ${escape(displayName)} · ${hh}:${mm}${privateBadge ? ' ' + privateBadge : ''}</div>
      <div class="text"></div>
    `;
    row.querySelector<HTMLDivElement>('.text')!.textContent = text;
    chatLog.appendChild(row);
    chatLog.scrollTop = chatLog.scrollHeight;
  }

  function roleBadge(role: Role): string {
    const label = role === 'host' ? 'host' : role === 'guest' ? 'ospite' : 'membro';
    return `<span class="role-badge role-${role}">${label}</span>`;
  }
  function renderPeople() {
    const rows = [...peers.values()].map((p) => `
      <div class="people-row">
        <div class="avatar-sm" style="background:${avatarGradient(p.id)}; color: var(--ala-gold);">${escape(initials(p.name))}</div>
        <div class="info">
          <strong>${escape(p.name)}${p.isSelf ? ' (tu)' : ''} ${roleBadge(p.role)}</strong>
        </div>
        ${selfRole === 'host' && !p.isSelf && p.role !== 'host' ? `
          <div class="people-actions">
            <button class="icon-btn-sm" data-people-act="mute" data-peer="${p.id}" title="Silenzia">${icon('mic-off', 'icon icon-sm')}</button>
            <button class="icon-btn-sm danger" data-people-act="kick" data-peer="${p.id}" title="Espelli">${icon('x', 'icon icon-sm')}</button>
          </div>
        ` : ''}
      </div>
    `).join('');
    // pending guests
    const pendingPlaceholder = '<div class="empty">Nessuno in sala d\'attesa</div>';
    peopleSec.innerHTML = `
      <div style="font-size:0.72rem; font-weight:700; letter-spacing:.1em; text-transform:uppercase; color: var(--rm-text-dim);">In stanza (${peers.size})</div>
      ${rows}
      ${selfRole === 'host' ? `
        <div style="font-size:0.72rem; font-weight:700; letter-spacing:.1em; text-transform:uppercase; color: var(--rm-text-dim); margin-top: 8px;">Sala d'attesa</div>
        <div id="pending-container"></div>
      ` : ''}
    `;
    if (selfRole === 'host') {
      const cont = peopleSec.querySelector<HTMLElement>('#pending-container')!;
      // ri-inserisci pending già conosciuti
      const existing = pendingMap;
      if (existing.size === 0) cont.innerHTML = pendingPlaceholder;
      else existing.forEach((name, id) => cont.appendChild(makePendingRow(id, name)));
    }
    peopleSec.querySelectorAll<HTMLButtonElement>('[data-people-act]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const act = btn.dataset.peopleAct!;
        const peerId = btn.dataset.peer!;
        const targetName = peers.get(peerId)?.name ?? 'questo partecipante';
        if (act === 'mute') {
          sig.request({ type: 'mutePeer', peerId }).catch((e) => {
            toastError('Silenziamento fallito', (e as Error).message, targetName);
          });
        }
        if (act === 'kick') {
          if (await ask(`Espellere ${targetName}?`, 'Espelli', 'Annulla', true)) {
            sig.request({ type: 'kickPeer', peerId, ban: false }).catch((e) => {
              toastError('Espulsione fallita', (e as Error).message, targetName);
            });
          }
        }
      });
    });
  }

  // pending guests management
  const pendingMap = new Map<string, string>();
  function appendWaitingGuest(peerId: string, name: string) {
    pendingMap.set(peerId, name);
    renderPeople();
  }
  function makePendingRow(peerId: string, name: string): HTMLElement {
    const div = document.createElement('div');
    div.className = 'pending-row';
    div.dataset.pending = peerId;
    div.innerHTML = `
      <div class="avatar-sm" style="background:${avatarGradient(peerId)}; color: var(--ala-gold);">${escape(initials(name))}</div>
      <div class="info" style="flex:1; min-width:0;">
        <strong>${escape(name)}</strong>
        <small>Ospite invitato</small>
      </div>
      <div class="people-actions">
        <button class="btn-deny" data-pact="deny" data-peer="${peerId}">Rifiuta</button>
        <button class="btn-admit" data-pact="admit" data-peer="${peerId}">${icon('check', 'icon icon-sm')} Ammetti</button>
      </div>
    `;
    div.querySelectorAll<HTMLButtonElement>('[data-pact]').forEach((b) => {
      b.addEventListener('click', () => {
        const act = b.dataset.pact!;
        const peerId = b.dataset.peer!;
        pendingMap.delete(peerId);
        if (act === 'admit') sig.request({ type: 'admitGuest', peerId }).catch(() => {});
        if (act === 'deny') sig.request({ type: 'denyGuest', peerId }).catch(() => {});
        renderPeople();
      });
    });
    return div;
  }

  // ── Capability detection: alcune piattaforme (iOS Safari) non supportano la
  // condivisione schermo. Nascondi il bottone — meglio di un click silente.
  const supportsScreen = typeof navigator.mediaDevices?.getDisplayMedia === 'function';
  if (!supportsScreen) btnScreen.style.display = 'none';

  // Traduzione errori server-side in messaggi user-friendly.
  // Ritorna `{ title, detail }`: quando il codice è mappato, `title` è il
  // messaggio user-friendly e `detail` è vuoto. Altrimenti il chiamante mette
  // come `title` un generico e `detail` ha il codice raw da mostrare al di sotto.
  function friendlyError(raw: string, fallbackTitle: string): { title: string; detail: string } {
    const map: Record<string, string> = {
      'cannot_kick_host': 'Non puoi espellere un altro host',
      'peer_not_found': 'Partecipante non trovato (forse è già uscito)',
      'forbidden': 'Solo l\'host può fare questa azione',
      'not joined': 'Non sei collegato alla riunione',
      'guest_not_waiting': 'L\'ospite non è più in sala d\'attesa',
      'already_recording': 'Una registrazione è già in corso',
      'banned': 'Sei stato bandito da questa stanza',
    };
    const mapped = map[raw];
    if (mapped) return { title: mapped, detail: '' };
    return { title: fallbackTitle, detail: raw };
  }
  function toastError(fallbackTitle: string, raw: string, name: string) {
    const { title, detail } = friendlyError(raw, fallbackTitle);
    const safeTitle = escape(title);
    const safeDetail = detail ? `<small>${escape(detail)}</small>` : '';
    pushToast(`<strong>${safeTitle}</strong>${safeDetail}`, name);
  }

  // Dialog di conferma custom: più affidabile di confirm() su iOS PWA standalone.
  function ask(question: string, okText = 'Conferma', cancelText = 'Annulla', danger = false): Promise<boolean> {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.className = 'ala-modal-overlay';
      overlay.innerHTML = `
        <div class="ala-modal-card">
          <p>${escape(question)}</p>
          <div class="ala-modal-actions">
            <button type="button" class="ala-modal-cancel">${escape(cancelText)}</button>
            <button type="button" class="ala-modal-ok ${danger ? 'danger' : ''}">${escape(okText)}</button>
          </div>
        </div>
      `;
      document.body.appendChild(overlay);
      const cleanup = (v: boolean) => { overlay.remove(); resolve(v); };
      overlay.querySelector<HTMLButtonElement>('.ala-modal-ok')!.addEventListener('click', () => cleanup(true));
      overlay.querySelector<HTMLButtonElement>('.ala-modal-cancel')!.addEventListener('click', () => cleanup(false));
      overlay.addEventListener('click', (e) => { if (e.target === overlay) cleanup(false); });
    });
  }

  // ── iOS autoplay unlock: i <video> dei remoti partono muted; al primo
  // user-gesture unmutiamo tutti i tile non-self e forziamo .play().
  let audioUnlocked = false;
  function unlockRemoteAudio() {
    if (audioUnlocked) return;
    audioUnlocked = true;
    for (const slot of peers.values()) {
      if (slot.isSelf) continue;
      const v = slot.tile.querySelector<HTMLVideoElement>('video');
      if (!v) continue;
      v.muted = false;
      v.play().catch((e) => console.warn('[room] remote .play() rejected:', e));
    }
    document.removeEventListener('click', unlockRemoteAudio, true);
    document.removeEventListener('touchend', unlockRemoteAudio, true);
  }
  document.addEventListener('click', unlockRemoteAudio, true);
  document.addEventListener('touchend', unlockRemoteAudio, true);

  // Forza .play() esplicito dopo srcObject (iOS può non auto-partire)
  function safePlay(v: HTMLVideoElement) {
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch((e) => console.warn('[room] video.play() rejected:', e));
  }

  // ── Mic/Cam/Screen/Hand toggles ──
  async function acquireMicTrack(): Promise<MediaStreamTrack | null> {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const t = s.getAudioTracks()[0] ?? null;
      if (t) props.micStream = new MediaStream([t]);
      return t;
    } catch (e) {
      toastError('Microfono non disponibile', (e as Error).message, selfName);
      return null;
    }
  }
  /**
   * Watchdog frame-flow del producer video. Risolve due bug osservati 13-06:
   *  (a) MOBILE join: track live ma framesSent=0 → freeze iniziale finche'
   *      l'utente non fa cam OFF→ON.
   *  (b) DESKTOP sfondo: track live, framesSent cresce, ma canvas emette
   *      frame interamente neri (lum=0).
   * Strategia: attesa 1.2s post-produce/replaceTrack, poi check stats E
   * (se effetto attivo) check contenuto canvas. Se uno dei due fallisce →
   * re-acquire camera + re-replace track. Idempotente, single-flight via lock.
   */
  /** Periodic watchdog publisher (dato decisivo 13-06 sera: mobile pubblica
   *  primo frame poi si blocca; il toggle cam manuale lo aggira). State
   *  hoistato sopra per evitare TDZ. Controllo ogni 3s: framesSent deve crescere. */
  function startPeriodicFrameFlowWatchdog() {
    if (frameFlowInterval) return;
    frameFlowInterval = setInterval(() => { void periodicCheck(); }, 3000);
  }
  function stopPeriodicFrameFlowWatchdog() {
    if (frameFlowInterval) { clearInterval(frameFlowInterval); frameFlowInterval = null; }
    consecutiveStalls = 0;
    lastFramesSent = 0;
  }
  async function periodicCheck(): Promise<void> {
    if (watchdogDisabled) return;
    if (!videoProducer || videoProducer.closed || videoProducer.paused) {
      consecutiveStalls = 0;
      lastFramesSent = 0;
      return;
    }
    const cur = await readFramesSent(videoProducer);

    // Black-canvas: solo se SIA stats fermi SIA canvas nero. Se l'effetto e'
    // attivo ma il canvas e' OK (lum>5), non scatenare recovery.
    if (currentEffect !== 'none' && effects) {
      const blk = effects.isCanvasBlack();
      if (blk && !isSelfViewFlowing()) {
        if (recoveryAttempts >= MAX_RECOVERY) {
          console.warn(`[room] periodic watchdog: canvas BLACK ma raggiunto MAX_RECOVERY (${MAX_RECOVERY}) → watchdog DISABLE per non loopare`);
          watchdogDisabled = true; return;
        }
        recoveryAttempts++;
        consecutiveStalls = 0; lastFramesSent = 0;
        console.warn(`[room] periodic watchdog: canvas BLACK (attempt ${recoveryAttempts}/${MAX_RECOVERY}) → recovering`);
        void verifyAndRecoverFrameFlow('periodic-canvas-black');
        return;
      }
    }

    if (cur <= lastFramesSent) {
      consecutiveStalls++;
      if (consecutiveStalls >= 2) {
        // SECOND-SIGNAL: se il self-view sta renderizzando frame, le stats
        // sono stale (bug 0.9.6-fix1: simulcast layer dormiente). NON recovery.
        if (isSelfViewFlowing()) {
          console.warn(`[room] periodic watchdog: framesSent stall=${cur} MA self-view scorre (lastFrameAge=${Date.now() - lastSelfFrameAt}ms) → ignoro (metric stale, possibile simulcast)`);
          consecutiveStalls = 0;
          lastFramesSent = cur;
          // Anche se la stat e' rotta, il video va: reset attempts cosi'
          // se piu' tardi capita un vero stall li' siamo pronti.
          recoveryAttempts = 0;
          return;
        }
        if (recoveryAttempts >= MAX_RECOVERY) {
          console.warn(`[room] periodic watchdog: ${MAX_RECOVERY} recovery senza miglioramento → watchdog DISABLE per non loopare`);
          watchdogDisabled = true; return;
        }
        recoveryAttempts++;
        consecutiveStalls = 0; lastFramesSent = 0;
        console.warn(`[room] periodic watchdog: framesSent fermo a ${cur} + self-view non scorre (attempt ${recoveryAttempts}/${MAX_RECOVERY}) → recovering`);
        void verifyAndRecoverFrameFlow('periodic-stall');
        return;
      }
    } else {
      // Crescita = pipeline sana → reset SIA stalli SIA tentativi recovery.
      consecutiveStalls = 0;
      recoveryAttempts = 0;
    }
    lastFramesSent = cur;
  }
  async function verifyAndRecoverFrameFlow(origin: string): Promise<void> {
    if (frameFlowLock) return;
    frameFlowLock = true;
    try {
      if (!videoProducer || videoProducer.closed) return;
      const before = await readFramesSent(videoProducer);
      // Window di 1.2s: copre setup del sender RTP + primi frame del canvas.
      await new Promise<void>((r) => setTimeout(r, 1200));
      if (!videoProducer || videoProducer.closed) return;
      const after = await readFramesSent(videoProducer);
      const framesGrew = after > before;
      let canvasBlack = false;
      if (currentEffect !== 'none' && effects) {
        // Sample del canvas: se l'effetto e' attivo e l'output e' nero,
        // anche se framesSent cresce, sto pubblicando frame neri.
        canvasBlack = effects.isCanvasBlack();
      }
      if (framesGrew && !canvasBlack) return; // tutto OK
      console.warn(`[room] frame-flow watchdog (${origin}): framesGrew=${framesGrew} canvasBlack=${canvasBlack} → recovering`);
      // Recovery: re-acquire camera fresh + re-apply pipeline.
      try {
        // Stop track corrente del props.camStream (non chiudo il producer).
        const oldStream = props.camStream;
        if (oldStream) {
          try { oldStream.getVideoTracks().forEach((t) => t.stop()); } catch { /* */ }
        }
        props.camStream = null;
        const newTrack = await acquireCamTrack();
        if (!newTrack) {
          console.warn('[room] frame-flow watchdog: acquireCamTrack ha fallito');
          return;
        }
        if (currentEffect !== 'none') {
          // Re-applica l'effetto: setInputStream con nuovo cam, ricostruisce
          // l'output dal canvas captureStream e fa replaceTrack del producer.
          await applyEffect(currentEffect);
        } else if (videoProducer && !videoProducer.closed) {
          await videoProducer.replaceTrack({ track: newTrack });
          const v = selfSlot.tile.querySelector<HTMLVideoElement>('video')!;
          v.srcObject = props.camStream;
          safePlay(v);
        }
      } catch (e) {
        console.warn('[room] frame-flow watchdog recovery error', e);
      }
    } finally {
      frameFlowLock = false;
    }
  }

  /**
   * Aggrega framesSent/framesEncoded su TUTTI gli outbound-rtp video del
   * producer (FIX 0.9.6-server fix2: simulcast camera ha 3 encodings r0/r1/r2;
   * stats.values() non garantisce ordine → leggendo solo il primo si rischia di
   * pescare un layer dormiente con counter fermo mentre quello attivo cresce).
   */
  async function readFramesSent(p: MC.Producer): Promise<number> {
    try {
      const stats = await p.getStats();
      let total = 0;
      for (const r of stats.values()) {
        if (r.type === 'outbound-rtp' && (r as { kind?: string }).kind === 'video') {
          const rr = r as { framesSent?: number; framesEncoded?: number };
          total += rr.framesSent ?? rr.framesEncoded ?? 0;
        }
      }
      return total;
    } catch { /* */ }
    return 0;
  }

  /** Second-signal sanity per il watchdog: il <video> self-view ha renderizzato
   *  un frame nell'ultimo intervallo? Se sì la pipeline locale e' viva. */
  function isSelfViewFlowing(maxAgeMs = 2000): boolean {
    return lastSelfFrameAt > 0 && (Date.now() - lastSelfFrameAt) < maxAgeMs;
  }
  /** Avvia il tracker rVFC sul self-view: ogni frame renderizzato aggiorna il
   *  timestamp. Idempotente (chiamato dopo il primo produce). Fallback no-op
   *  se rVFC non disponibile (vecchi browser/WebKit). */
  function startSelfViewFrameTracker(): void {
    const v = selfSlot.tile.querySelector<HTMLVideoElement>('video');
    if (!v || typeof (v as HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => number }).requestVideoFrameCallback !== 'function') {
      // Senza rVFC, il second-signal non e' affidabile → trattalo come "viable"
      // perché meglio non scatenare recovery falsi positivi. Setta un timestamp
      // sempre fresco. NB: questo riduce il watchdog a un check piu' permissivo.
      lastSelfFrameAt = Date.now();
      setInterval(() => { lastSelfFrameAt = Date.now(); }, 1000);
      return;
    }
    const rVfc = (v as HTMLVideoElement & { requestVideoFrameCallback: (cb: () => void) => number }).requestVideoFrameCallback.bind(v);
    const cb = () => {
      lastSelfFrameAt = Date.now();
      try { rVfc(cb); } catch { /* */ }
    };
    try { rVfc(cb); } catch { /* */ }
  }

  async function acquireCamTrack(): Promise<MediaStreamTrack | null> {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } } });
      const t = s.getVideoTracks()[0] ?? null;
      if (t) {
        props.camStream = new MediaStream([t]);
        const v = selfSlot.tile.querySelector<HTMLVideoElement>('video')!;
        v.srcObject = props.camStream;
        v.muted = true;
      }
      return t;
    } catch (e) {
      toastError('Camera non disponibile', (e as Error).message, selfName);
      return null;
    }
  }

  /** True se almeno una videoTrack di stream è in stato 'live'. */
  function isCamStreamLive(stream: MediaStream | null | undefined): boolean {
    if (!stream) return false;
    return stream.getVideoTracks().some((t) => t.readyState === 'live');
  }
  /**
   * Garantisce che props.camStream contenga una track video viva. Se la track
   * memorizzata è 'ended' (browser ha rilasciato la camera per pausa, OS sleep,
   * o sospensione), ne acquisisce una nuova via gUM.
   */
  async function ensureLiveCamStream(): Promise<MediaStream | null> {
    if (isCamStreamLive(props.camStream)) return props.camStream;
    const newT = await acquireCamTrack();
    return newT ? props.camStream : null;
  }

  async function toggleMic() {
    if (!audioProducer) {
      const t = props.micStream?.getAudioTracks()[0] ?? await acquireMicTrack();
      if (!t) return;
      try {
        audioProducer = await sendTransport.produce({ track: t, codecOptions: { opusStereo: true, opusDtx: true }, appData: { source: 'mic' } });
      } catch (e) {
        toastError('Errore avvio microfono', (e as Error).message, selfName);
        return;
      }
      props.prefs.micOn = true;
    } else if (audioProducer.paused) {
      await audioProducer.resume();
      sig.request({ type: 'resumeProducer', producerId: audioProducer.id }).catch(() => {});
      props.prefs.micOn = true;
    } else {
      await audioProducer.pause();
      sig.request({ type: 'pauseProducer', producerId: audioProducer.id }).catch(() => {});
      props.prefs.micOn = false;
    }
    btnMic.classList.toggle('off', !props.prefs.micOn);
    btnMic.innerHTML = `<span class="tooltip">Microfono (M)</span>${icon(props.prefs.micOn ? 'mic' : 'mic-off', 'icon icon-lg')}`;
    localStorage.setItem('ala-meet.mic', String(props.prefs.micOn));
    updateBadge(selfSlot, 'muted', !props.prefs.micOn);
  }
  async function toggleCam() {
    if (!videoProducer) {
      const stream = await ensureLiveCamStream();
      const t = stream?.getVideoTracks()[0];
      if (!t) return;
      try {
        videoProducer = await sendTransport.produce({
          track: t,
          encodings: cameraEncodings(),
          codecOptions: { videoGoogleStartBitrate: 1000 },
          appData: { source: 'camera' },
        });
      } catch (e) {
        toastError('Errore avvio camera', (e as Error).message, selfName);
        return;
      }
      props.prefs.camOn = true;
      // Se c'era un effetto persistito, riapplicalo SULLA NUOVA TRACK.
      if (currentEffect !== 'none') queueMicrotask(() => { void applyEffect(currentEffect); });
      else void verifyAndRecoverFrameFlow('toggleCam-produce');
      // Producer ricreato → reset stato watchdog (anche se era DISABLEd) e ri-arm.
      watchdogDisabled = false;
      recoveryAttempts = 0;
      consecutiveStalls = 0;
      lastFramesSent = 0;
      startSelfViewFrameTracker();
      startPeriodicFrameFlowWatchdog();
    } else if (videoProducer.paused) {
      // RESUME: verifica che la track del producer sia ancora viva. Se il browser
      // l'ha rilasciata (camera handle perso, OS sleep, lunga inattività), bisogna
      // ricrearla e fare replaceTrack PRIMA di resume — altrimenti l'effetto
      // gira sullo stream vecchio o si vede solo lo sfondo dipinto.
      const currentTrack = videoProducer.track;
      const trackStale = !currentTrack || currentTrack.readyState !== 'live';
      if (trackStale) {
        const stream = await ensureLiveCamStream();
        const fresh = stream?.getVideoTracks()[0];
        if (fresh) {
          try { await videoProducer.replaceTrack({ track: fresh }); }
          catch (e) { toastError('Errore avvio camera', (e as Error).message, selfName); return; }
        }
      }
      await videoProducer.resume();
      sig.request({ type: 'resumeProducer', producerId: videoProducer.id }).catch(() => {});
      props.prefs.camOn = true;
      // Se c'è un effetto attivo, RICOLLEGA la pipeline al nuovo stream e
      // rigenera l'output (replaceTrack sul producer + srcObject sul self tile).
      if (currentEffect !== 'none') queueMicrotask(() => { void applyEffect(currentEffect); });
      else void verifyAndRecoverFrameFlow('toggleCam-resume');
    } else {
      await videoProducer.pause();
      sig.request({ type: 'pauseProducer', producerId: videoProducer.id }).catch(() => {});
      props.prefs.camOn = false;
    }
    btnCam.classList.toggle('off', !props.prefs.camOn);
    btnCam.innerHTML = `<span class="tooltip">Camera (V)</span>${icon(props.prefs.camOn ? 'cam' : 'cam-off', 'icon icon-lg')}`;
    selfSlot.tile.classList.toggle('cam-off', !props.prefs.camOn);
    localStorage.setItem('ala-meet.cam', String(props.prefs.camOn));
  }
  async function toggleScreen() {
    if (!supportsScreen) {
      pushToast(`<strong>Condivisione schermo non supportata</strong><small>Il tuo browser/dispositivo non la supporta</small>`, selfName);
      return;
    }
    if (screenProducer) {
      try { await sig.request({ type: 'closeProducer', producerId: screenProducer.id }); } catch { /* */ }
      screenProducer.close(); screenProducer = undefined;
      btnScreen.classList.remove('active');
      btnScreen.innerHTML = `<span class="tooltip">Condividi schermo</span>${icon('screen', 'icon icon-lg')}`;
      exitPresenter();
      return;
    }
    let s: MediaStream;
    try { s = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 15 }, audio: false }); }
    catch (e) {
      toastError('Schermo non condiviso', (e as Error).message, selfName);
      return;
    }
    const track = s.getVideoTracks()[0];
    if (!track) return;
    screenProducer = await sendTransport.produce({
      track,
      encodings: [{ maxBitrate: 1_500_000 }],
      codecOptions: { videoGoogleStartBitrate: 1200 },
      appData: { source: 'screen' },
    });
    track.onended = () => { void toggleScreen(); };
    btnScreen.classList.add('active');
    selfSlot.screenStream = new MediaStream([track]);
    selfSlot.hasScreen = true;
    enterPresenter(selfId);
  }
  let handState = false;
  function toggleHand() {
    handState = !handState;
    btnHand.classList.toggle('active', handState);
    updateBadge(selfSlot, 'hand', handState);
    sig.request({ type: 'chatMessage', text: `${HAND_PREFIX}${handState ? 'up' : 'down'}]` }).catch(() => {});
  }
  let recState = false;
  async function toggleRec() {
    if (!recState) {
      if (!await ask('Avviare la registrazione? Tutti i partecipanti verranno notificati.', 'Registra', 'Annulla')) return;
      try { await sig.request({ type: 'startRecording' }); recState = true; }
      catch (e) {
        toastError('Registrazione fallita', (e as Error).message, '');
      }
    } else {
      if (!await ask('Stop registrazione?', 'Ferma', 'Annulla')) return;
      try { await sig.request({ type: 'stopRecording' }); recState = false; }
      catch (e) { console.warn(e); }
    }
  }

  // REC indicator
  function showRec(startedBy: string) {
    recInd.hidden = false;
    recInd.title = `Avviata da ${startedBy}`;
    recState = true;
    btnRecord.classList.add('active');
  }
  function hideRec() {
    recInd.hidden = true;
    recState = false;
    btnRecord.classList.remove('active');
  }

  function pushToast(html: string, name: string) {
    const div = document.createElement('div');
    div.className = 'toast';
    div.innerHTML = `<span class="avatar-sm" style="background:${avatarGradient(name)}; color: var(--ala-gold); width:30px; height:30px; font-size:.78rem;">${escape(initials(name || '?'))}</span><div>${html}</div>`;
    toasts.appendChild(div);
    setTimeout(() => div.remove(), 4500);
  }

  function endCall(reason: 'local' | 'roomClosed' | 'kicked') {
    if (endedReason) return; // idempotente
    endedReason = reason;
    // Per il leave volontario notifichiamo il server prima di chiudere.
    if (reason === 'local') {
      try { sig.request({ type: 'leave' }).catch(() => {}); } catch { /* */ }
    }
    cleanup();
    cb.onEnded({
      startedAt, endedAt: Date.now(),
      peakPeerCount, myName: selfName, roomLabel,
    });
  }

  function cleanup() {
    clearInterval(metaTimer);
    clearInterval(netTimer);
    stopPeriodicFrameFlowWatchdog();
    try { effects?.dispose(); } catch { /* */ }
    effects = null;
    try { audioProducer?.close(); } catch { /* */ }
    try { videoProducer?.close(); } catch { /* */ }
    try { screenProducer?.close(); } catch { /* */ }
    try { sendTransport.close(); } catch { /* */ }
    try { recvTransport.close(); } catch { /* */ }
    try { sig.close(); } catch { /* */ }
    props.micStream?.getTracks().forEach((t) => t.stop());
    props.camStream?.getTracks().forEach((t) => t.stop());
    for (const slot of peers.values()) {
      slot.cameraStream.getTracks().forEach((t) => t.stop());
      slot.screenStream.getTracks().forEach((t) => t.stop());
    }
  }
}

// ── Helpers ──

// Detection: WebKit/Safari (incluso iOS qualsiasi browser, che usa WebKit forzato)
function isWebKit(): boolean {
  const ua = navigator.userAgent;
  // iOS = qualsiasi browser su iPhone/iPad usa WebKit (CriOS, FxiOS, EdgiOS).
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // macOS Safari (NON Chrome né Edge né Firefox)
  if (/Safari\//.test(ua) && !/Chrom|CriOS|Edg|Firefox|FxiOS/.test(ua)) return true;
  return false;
}

// Encodings camera: 3-layer simulcast su Chromium/Firefox.
// Su WebKit (Safari/iOS) il simulcast multi-layer ha bug noti che riducono la
// qualità ricevuta dagli altri peer al solo layer base (r0). Usiamo invece un
// singolo encoding ad alta bitrate, così gli altri peer ricevono tutto in HD.
function cameraEncodings(): RTCRtpEncodingParameters[] {
  if (isWebKit()) {
    return [{ maxBitrate: 1_500_000 }];
  }
  return [
    { rid: 'r0', maxBitrate: 100_000, scaleResolutionDownBy: 4 },
    { rid: 'r1', maxBitrate: 300_000, scaleResolutionDownBy: 2 },
    { rid: 'r2', maxBitrate: 900_000, scaleResolutionDownBy: 1 },
  ];
}

async function createSendTransport(sig: LobbyResult['signaling'], device: Device, iceServers: RTCIceServer[]) {
  const params = await sig.request<MC.TransportOptions>({ type: 'createWebRtcTransport', direction: 'send' });
  const t = device.createSendTransport({ ...params, iceServers });
  t.on('connect', ({ dtlsParameters }, ok, err) => {
    sig.request({ type: 'connectWebRtcTransport', transportId: t.id, dtlsParameters }).then(() => ok()).catch(err);
  });
  t.on('produce', ({ kind, rtpParameters, appData }, ok, err) => {
    sig.request<{ id: string }>({ type: 'produce', transportId: t.id, kind, rtpParameters, appData })
      .then((res) => ok({ id: res.id })).catch(err);
  });
  return t;
}
async function createRecvTransport(sig: LobbyResult['signaling'], device: Device, iceServers: RTCIceServer[]) {
  const params = await sig.request<MC.TransportOptions>({ type: 'createWebRtcTransport', direction: 'recv' });
  const t = device.createRecvTransport({ ...params, iceServers });
  t.on('connect', ({ dtlsParameters }, ok, err) => {
    sig.request({ type: 'connectWebRtcTransport', transportId: t.id, dtlsParameters }).then(() => ok()).catch(err);
  });
  return t;
}

function computeSelfName(props: RoomProps): string {
  // displayName che è stato propagato via join: not directly available qui;
  // fallback: peerId iniziale del lobby.
  // Il joinResp non include lo displayName del peer corrente, ma e' nel token.
  const tokenInput = document.getElementById('token') as HTMLInputElement | null;
  if (tokenInput?.value) {
    try {
      const p = JSON.parse(atob(tokenInput.value.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (p.displayName) return p.displayName;
    } catch { /* */ }
  }
  return 'Tu';
}

function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('') || '??';
}
function avatarGradient(seed: string): string {
  let h = 0; for (const c of seed) h = (h * 31 + c.charCodeAt(0)) | 0;
  const palettes = [
    'linear-gradient(145deg, #2c5b8a, #1a3c61)',
    'linear-gradient(145deg, #8a5b2c, #5e3d1d)',
    'linear-gradient(145deg, #2c8a4d, #1a5b34)',
    'linear-gradient(145deg, #6a2c8a, #41205e)',
    'linear-gradient(145deg, #2c8a8a, #1a5b5b)',
    'linear-gradient(145deg, #8a2c5b, #5e1d3d)',
  ];
  return palettes[Math.abs(h) % palettes.length]!;
}
function roleLabel(r: Role): string { return r === 'host' ? 'host' : r === 'guest' ? 'ospite' : 'membro'; }
function escape(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
function formatDuration(ms: number): string {
  const s = Math.floor(ms / 1000); const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60); const sec = s % 60;
  if (h > 0) return `${pad(h)}:${pad(m)}:${pad(sec)}`;
  return `${pad(m)}:${pad(sec)}`;
}
function pad(n: number): string { return String(n).padStart(2, '0'); }
